import { Locale, TenantStatus } from '../enums';
import { TENANT_FEATURE_KEYS, TenantFeatureKey } from '../utils/tenantFeatures';
import { z } from 'zod';

/**
 * A stored asset FILENAME — never a path.
 *
 * These two values are concatenated into S3 keys (`<id>/logo/<name>`,
 * `_staging/<name>`) and into the URLs handed to every visitor, and they arrive
 * as client-supplied strings. Promotion already refuses a name whose object is
 * not there, so this is not what stops a stranger's file being adopted; what it
 * stops is a name that is not a filename at all. S3 does not normalise `..` or
 * `/`, so `../cover-photo/x.png` is stored verbatim and the column then points
 * at a key OUTSIDE the folder its own prefix names — invisible to
 * `branding-asset-references`, whose reference set is built from
 * `<id>/<folder>/<name>`, so the prune script would count the live asset as
 * garbage.
 *
 * Deliberately a character class rather than the presign's exact
 * `<uuid>.<ext>`: the shape of the name is this schema's business, minting it
 * is not, and pinning the uuid form here would reject every asset stored before
 * that convention existed the moment anything re-sent one.
 */
export const STORED_ASSET_NAME_MAX_LENGTH = 128;
export const STORED_ASSET_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

/**
 * The same rule as a PREDICATE, for the readers that take an asset filename
 * from somewhere a DTO never sees.
 *
 * There is one: a `TenantSettings.manifest` override may name its own icons,
 * and `resolveTenantManifestOverride` resolves a bare `src` against
 * `<id>/logo/` exactly like `logoName` — a third path into the same bucket, and
 * the only one the zod schema above cannot cover. Left unchecked it reproduces
 * precisely the defect this rule exists to stop: `../cover-photo/x.png`
 * resolves to a key OUTSIDE the folder its prefix names, so the URL escapes the
 * logo folder while `branding-asset-references` records the unnormalised
 * `<id>/logo/../cover-photo/x.png` — which matches no real object, leaving the
 * live one looking like garbage to `--delete`.
 */
export const isStoredAssetName = (name: unknown): name is string =>
  typeof name === 'string' &&
  name.length > 0 &&
  name.length <= STORED_ASSET_NAME_MAX_LENGTH &&
  STORED_ASSET_NAME_RE.test(name);

const StoredAssetName = z
  .string()
  .min(1)
  .max(STORED_ASSET_NAME_MAX_LENGTH)
  .regex(STORED_ASSET_NAME_RE, 'VALIDATION__InvalidAssetName');

export const CreateTenantDto = z
  .object({
    canHaveAdditionalOrganizations: z.boolean(),
    canHaveIndividualTrainers: z.boolean(),
    domain: z
      .string()
      .min(3, 'VALIDATION__Min3CharLimit')
      .max(255, 'VALIDATION__Max255CharLimit'),
    // The brand: what mail and the manifest show. Same rule as UPDATE. Firebase's
    // narrower display name is derived from it on the server.
    name: z
      .string()
      .trim()
      .min(3, 'VALIDATION__Min3CharLimit')
      .max(255, 'VALIDATION__Max255CharLimit'),
    // Optional, and normally absent: a new tenant starts on the catalog
    // defaults and its palette is set afterwards in the Branding tab. The
    // create form used to send the creating admin's whole live runtime theme
    // here, which both copied that admin's org rebrand into the new tenant and
    // stored the derived vars verbatim. Kept accepted (and stripped by
    // `serializeTenantTheme`) so an API client seeding a palette still works.
    theme: z.record(z.any(), z.any()).optional(),
    // NO `logoName` / `coverPhoto` here, unlike `theme`. Those two name an
    // UPLOADED OBJECT, and the only proof the server ever gets that the
    // browser's PUT happened is promoting that object out of `_staging/` — which
    // needs the tenant id, and the id does not exist until this route has
    // created the row. A filename accepted here would therefore be stored
    // unpromoted: the column would point at `<id>/logo/<uuid>` while the bytes
    // sat in staging until the lifecycle rule deleted them, so the new tenant's
    // logo 404s for every visitor and icon generation fails silently.
    // Branding is set right after creation through `PATCH :id/branding`, which
    // promotes — the flow the admin UI already uses.
    //
    // The three caps below are the COLUMN's, restated. `shortName`,
    // `splashScreenText` and `keywords` are all `DataTypes.STRING`, i.e.
    // varchar(255), and they were the only string fields here with no bound —
    // `domain` and `name` have carried one all along. Unbounded, an over-long
    // value passes validation, reaches Postgres, and comes back as "value too
    // long"; `updateInstanceWithAuditLog` swallows that, so what the admin sees
    // is `persistTenantChange`'s generic 500 ("could not be saved, try again")
    // with no field marked and nothing to correct. With the cap it is a
    // translated error on the field that is actually wrong.
    shortName: z.string().max(255, 'VALIDATION__Max255CharLimit'),
    description: z.string().optional(),
    manifest: z.record(z.string(), z.any()).nullable().optional(),
    splashScreenText: z
      .string()
      .max(255, 'VALIDATION__Max255CharLimit')
      .optional(),
    keywords: z.string().max(255, 'VALIDATION__Max255CharLimit'),
  })
  .strict();

export type ApiCreateTenantDto = z.infer<typeof CreateTenantDto>;

const contentLabel = z
  .string()
  .trim()
  .max(255, 'VALIDATION__Max255CharLimit')
  .nullable()
  .optional()
  .transform((value) => (value === '' ? null : value));

export const UpdateTenantDto = z
  .object({
    // NO `canHaveAdditionalOrganizations` / `canHaveIndividualTrainers` and NO
    // `features` here on purpose. They have exactly one writer,
    // `PUT :tenantId/features` (`UpdateTenantFeaturesDto`), which writes the
    // two columns and the six switches together in one audited update. This
    // route used to accept the two flags, and `.strict()` is what now turns a
    // direct PATCH of any of the three into a refusal rather than a quiet way
    // round the panel (or a form snapshot overwriting what the panel saved).
    domain: z
      .string()
      .min(3, 'VALIDATION__Min3CharLimit')
      .max(255, 'VALIDATION__Max255CharLimit')
      .optional(),
    name: z
      .string()
      .min(3, 'VALIDATION__Min3CharLimit')
      .max(255, 'VALIDATION__Max255CharLimit')
      .optional(),
    // NO `theme` here on purpose. Colour VALUES have exactly one writer,
    // `PUT /v2/tenants/rebrand-theme`, which merges rather than replaces
    // (`POST /v2/tenants` may seed the column at birth, and goes through the
    // same `serializeTenantTheme`). This route used to full-replace the column
    // from a form snapshot, so the two disagreed about both semantics and
    // permissions.
    logoName: StoredAssetName.nullable().optional(),
    coverPhoto: StoredAssetName.nullable().optional(),
    // Same column caps as CREATE — see the note there.
    shortName: z.string().max(255, 'VALIDATION__Max255CharLimit').optional(),
    description: z.string().nullable().optional(),
    // `z.any()` values, like CREATE. It was `z.record(z.string(), z.string())`,
    // which refuses every non-string member a web app manifest actually has —
    // `icons` (an array) above all. So a row seeded with icons at create time
    // could never be written again: reading the tenant and PATCHing it back
    // unchanged answered 400, and the manifest-icon machinery around this
    // column (the `src` resolver, the prune reference set) worked on a shape
    // only one of the two routes could produce.
    manifest: z.record(z.string(), z.any()).nullable().optional(),
    splashScreenText: z
      .string()
      .max(255, 'VALIDATION__Max255CharLimit')
      .optional(),
    // How the tenant names its platform content (free for every organization
    // of the tenant, e.g. "MYFITWORLD content") and its premium content in
    // the UI. Blank or null = the app's default wording.
    platformContentLabel: contentLabel,
    premiumContentLabel: contentLabel,
    // Q5 / Task 9 (Ruling R14) — WHO ISSUES this tenant's payment
    // confirmations. The only writer of `TenantSettings.billingIdentity`, and
    // deliberately THIS route rather than a new one: it is already the tenant
    // settings chokepoint and it already gates on the explicit super-admin
    // flag (`onlySuperAdmin`) rather than on a permission key a custom org
    // role could carry.
    //
    // Every part optional and country-agnostic, matching the `BillingIdentity`
    // shape: one free-form name, free-form address lines, ONE tax id whatever
    // the local one is called, and two contact fields. `.strict()` so a
    // misspelled member is a 422 rather than a silently ignored one, and
    // `nullable` so the identity can be cleared.
    //
    // It changes nothing already issued: the value is frozen into
    // `Invoices.issuerSnapshot` at each invoice's creation, so correcting it
    // here reaches only invoices issued afterwards.
    billingIdentity: z
      .object({
        legalName: z.string().trim().max(255).optional(),
        addressLines: z.array(z.string().trim().max(255)).max(6).optional(),
        taxId: z.string().trim().max(255).optional(),
        email: z.string().trim().email().max(255).optional(),
        website: z.string().trim().max(255).optional(),
      })
      .strict()
      .nullable()
      .optional(),
    // Who gets this tenant's operational mail: disputes, seller requests,
    // organizations over their stair. Null = our team, through the env address.
    opsEmail: z.string().trim().email().max(255).nullable().optional(),
    defaultLocale: z.nativeEnum(Locale).nullable().optional(),
    keywords: z.string().max(255, 'VALIDATION__Max255CharLimit'),
  })
  .strict();

export type ApiUpdateTenantDto = z.infer<typeof UpdateTenantDto>;

/**
 * The ONE write body for a tenant's switches (`PUT :tenantId/features`): all
 * eight booleans, required. Six are the feature switches; the other two are the
 * organization flags, which stay their own columns. `.strict()` and no
 * coercion, so an unknown key, a missing field, `null` and `'false'` are all
 * refused. The server normalizes the six into the sparse stored object
 * (`toStoredTenantFeatures`) and writes it with the two columns in one update.
 */
export const UpdateTenantFeaturesDto = z
  .object({
    ...(Object.fromEntries(
      TENANT_FEATURE_KEYS.map((key) => [key, z.boolean()]),
    ) as Record<TenantFeatureKey, z.ZodBoolean>),
    canHaveAdditionalOrganizations: z.boolean(),
    canHaveIndividualTrainers: z.boolean(),
  })
  .strict();

export type ApiUpdateTenantFeaturesDto = z.infer<
  typeof UpdateTenantFeaturesDto
>;

export type TenantReadinessKey =
  | 'billingIdentity'
  | 'categories'
  | 'coverPhoto'
  | 'firebaseTenant'
  | 'logo'
  | 'opsEmail'
  | 'premiumPlans'
  | 'priceCatalog'
  | 'privacyPolicy'
  | 'stripe';

export type TenantReadinessStatus = 'missing' | 'ok' | 'unknown';

/** One line of `GET /tenants/:id/readiness`: what a tenant still lacks. */
export type TenantReadinessItem = {
  key: TenantReadinessKey;
  status: TenantReadinessStatus;
};

/**
 * Branding-only tenant write: the surface a `platform_admin` gets on
 * /admin/tenants/:id. Identity (name, domain, status, flags) stays on the
 * super-admin-only PATCH; colours go through the rebrand-theme route.
 */
export const UpdateTenantBrandingDto = z
  .object({
    logoName: StoredAssetName.nullable().optional(),
    coverPhoto: StoredAssetName.nullable().optional(),
  })
  .strict();

export type ApiUpdateTenantBrandingDto = z.infer<
  typeof UpdateTenantBrandingDto
>;

export const FindTenantDto = z
  .object({
    canHaveAdditionalOrganizations: z.boolean().optional(),
    canHaveIndividualTrainers: z.boolean().optional(),
    domain: z.string().optional(),
    name: z.string().optional(),
    status: z.string().toUpperCase().pipe(z.nativeEnum(TenantStatus)),
    tenantId: z.string().uuid().optional(),
    keywords: z.string(),
  })
  .strict();

export type ApiFindTenantDto = z.infer<typeof FindTenantDto>;

export const TenantDto = CreateTenantDto.extend({
  status: z.string().toUpperCase().pipe(z.nativeEnum(TenantStatus)),
  id: z.string(),
  splashScreenText: z.string().optional(),
});

export type ApiTenantDto = z.infer<typeof TenantDto>;

/**
 * A tenant's premium catalog price, set by a super admin (owner's decision
 * 2026-11-01, P2): it charges nothing and touches no subscription, since a subscriber
 * keeps the price snapshotted at subscribe.
 */
export const UpdatePremiumPlanPriceDto = z.object({
  priceMinor: z.number().int().min(100).max(100_000_000),
});
export type ApiUpdatePremiumPlanPriceDto = z.infer<
  typeof UpdatePremiumPlanPriceDto
>;

export const StartTenantContentHandoverDto = z.object({
  authorEmail: z.string().email('VALIDATION__InvalidEmail'),
});
export type ApiStartTenantContentHandoverDto = z.infer<
  typeof StartTenantContentHandoverDto
>;

export type TenantPremiumPlan = {
  active: boolean;
  currency: string;
  id: string;
  interval: string;
  priceMinor: string;
};

export type TenantContentHandoverView = {
  counts: Record<'copied' | 'failed' | 'pending' | 'skipped', number>;
  finishedAt: null | string;
  problems: {
    contentType: string;
    reason: null | string;
    sourceId: string;
    status: string;
  }[];
  startedAt: string;
  status: 'done' | 'running';
};

export type TenantContentHandoverState = {
  handover: null | TenantContentHandoverView;
  /** The tenant the library comes from cannot receive it. */
  isSourceTenant: boolean;
};
